import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Slide } from '@mui/material';

const ToastContext = createContext();

export function useToast() {
    return useContext(ToastContext);
}

function SlideTransition(props) {
    return <Slide {...props} direction="up" />;
}

export function ToastProvider({ children }) {
    const [toast, setToast] = useState({
        open: false,
        message: '',
        severity: 'success', // 'success' | 'error' | 'warning' | 'info'
        duration: 4000,
    });

    const showToast = useCallback((message, severity = 'success', duration = 4000) => {
        setToast({
            open: true,
            message,
            severity,
            duration,
        });
    }, []);

    const hideToast = useCallback(() => {
        setToast(prev => ({ ...prev, open: false }));
    }, []);

    const success = useCallback((message) => showToast(message, 'success'), [showToast]);
    const error = useCallback((message) => showToast(message, 'error'), [showToast]);
    const warning = useCallback((message) => showToast(message, 'warning'), [showToast]);
    const info = useCallback((message) => showToast(message, 'info'), [showToast]);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast((prev) => ({ ...prev, open: false }));
    };

    return (
        <ToastContext.Provider value={{ showToast, hideToast, success, error, warning, info }}>
            {children}
            <Snackbar
                open={toast.open}
                autoHideDuration={toast.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                TransitionComponent={SlideTransition}
            >
                <Alert
                    onClose={handleClose}
                    severity={toast.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        minWidth: '300px',
                        fontWeight: 500,
                        boxShadow: '0 12px 28px rgba(11, 32, 56, 0.14)',
                        borderRadius: '8px',
                    }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
}

export default ToastProvider;
